import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { AnyPartType } from "../constants/partTypes";
import { LanguageMap, useLanguage } from "../i18n/useLanguage";
import { DealerVehicleResultsRoute } from "../navigation/DealerRoutes";
import { useRouteParams } from "../navigation/useRouteParams";
import { useDealerId } from "../stores/hooks/useDealerId";
import { useNationalAccountId } from "../stores/hooks/useNationalAccountId";
import { useSearchForm } from "../stores/hooks/useSearchForm";
import { ComboBoxOption } from "../ui/ComboBox/useComboBox";
import { productTypeToPartType } from "../util/productTypeToPartType";
import { useGetVehicleModels } from "./useGetVehicleModels";

export type SubmodelOption = ComboBoxOption<string, VehicleSubmodel> & {
  selected: boolean;
};

export const useGetVehicleSubmodels = (): ModifiedSWRResponse<
  FetcherResult<Payload>,
  SubmodelOption[]
> & {
  partType: AnyPartType | null;
  selectedSubmodel?: SubmodelOption;
  vehicleManufacturerId?: number;
  vehicleModelId?: string;
  vehicleYearId?: number;
} => {
  const {
    selectedVehicleModel,
    vehicleManufacturerId,
    vehicleModelId,
    vehicleYearId,
  } = useGetVehicleModels();
  const [language] = useLanguage();
  const params = useRouteParams(DealerVehicleResultsRoute);
  const [
    {
      productType,
      vehicleModelClassId,
      vehicleModelName,
      vehicleSubmodel,
      vehicleSubmodelSelected,
    },
  ] = useSearchForm();
  const partType = productTypeToPartType(productType);
  const nationalAccountId = useNationalAccountId();
  const dealerId = useDealerId();

  const { data, ...res } = useSwrGet<Payload>(
    vehicleModelName
      ? "/retailSiteWebService/vehicle/getVehicleSubmodels.php"
      : null,
    {
      dealerId,
      nationalAccountId,
      vehicleCategoryId:
        vehicleModelClassId || selectedVehicleModel?.vehicleModelClassId || "",
      vehicleManufacturerId: String(vehicleManufacturerId || ""),
      vehicleModelId: String(vehicleModelId || ""),
      vehicleYearId: String(vehicleYearId || ""),
    }
  );

  if (!data) {
    return {
      ...res,
      data: undefined,
      partType,
    };
  }

  const options = data.map((option) => {
    const label = option.vehicleSubmodelName[language];
    return {
      data: option,
      description: option.vehicleSubmodelTiresizeArray
        .map(({ formattedTireSize }) => formattedTireSize)
        .join(", "),
      label,
      selected:
        label.toLowerCase() ===
        (
          vehicleSubmodelSelected ||
          vehicleSubmodel ||
          params?.vehicleSubmodel
        )?.toLowerCase(),
      value: option.vehicleSubmodelId,
    };
  });

  const selectedSubmodel = options.find(({ selected }) => selected);

  return {
    ...res,
    data: options,
    partType,
    selectedSubmodel,
    vehicleManufacturerId,
    vehicleModelId,
    vehicleYearId,
  };
};

type Payload = VehicleSubmodel[];

export type VehicleSubmodel = {
  vehicleChassisNumber?: string;
  vehicleId: string;
  vehicleRequiresRunFlatTires: string;
  vehicleSubmodelId: string;
  vehicleSubmodelImage: string;
  vehicleSubmodelName: LanguageMap;
  vehicleSubmodelSortOrder: string;
  vehicleSubmodelTiresizeArray: SubmodelTireSize[];
};

export type SubmodelTireSize = {
  fitmentCode: string;
  fitmentIsDRW: string;
  fitmentIsStaggered: string;
  formattedTireSize: string;
  hasWheels: string;
  secondaryFormattedTireSize?: string;
  secondaryHasWheels?: string;
  tiresizeTypeDescription: LanguageMap;
  vehicleSubmodelTiresizeId: string;
};

/**
 * @see docs/worklog/TW-967-hunter-vin-results-vehicle-tire-sizes/rca.md
 * @see docs/worklog/TW-967-hunter-vin-results-vehicle-tire-sizes/rfc.md
 * @see docs/worklog/TW-967-hunter-vin-results-vehicle-tire-sizes/tfc.md
 */
